import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../context/UserContext';
import { useInVenueUsers } from '../../../context/useInVenueUsers';
import axios from 'axios';
import { Search, ShoppingCart } from 'lucide-react';
import CarritoPanel from './CarritoPanel';
import CartCard from '../../Elements/CartCard';
import SectionHeader from '../../Elements/SectionHeader';
import Button from '../../Elements/Button';
import EmptyState from '../../Elements/EmptyState';
import useConfirmar from '../../Elements/useConfirmar';
import { avisarCarritosCambiados } from '../../../lib/carritosBus';
import { filtroBusqueda, socioNum } from '../../../lib/helpers';
import { calcularTotalesCarrito } from '../../../lib/cartTotals';
import { parseMemberDiscount } from '../../../lib/helpers/memberDiscount';
import { cantidadDespachada, notaPedidoMerma } from '../../../lib/helpers/products';

/**
 * Carritos = unpaid transactions (source of truth: same as Contabilidad).
 * Shows draft transactions (Dispensario / Productos) with estado "Debe".
 * Same structure as Contabilidad: one row per transaction, expand/collapse for products.
 */
export default function Carritos() {
  const navigate = useNavigate();
  const [token, , , venueID] = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterUserId = searchParams.get('user_id') || null;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentTx, setPaymentTx] = useState(null);
  const { confirmar, dialogo: dialogoConfirmar } = useConfirmar();
  // Una sola tarjeta abierta a la vez: con varias desplegadas la rejilla se
  // descuadra y hay que buscar dónde estaba cada socio.
  const [abierto, setAbierto] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const { usersInVenue, updateUserCredit, refresh: refreshUsersInClub } = useInVenueUsers(token, venueID);

  const fetchTransactions = useCallback(async (silent = false) => {
    if (!venueID || !token) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get('/v1/venue-draft-carts', {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransactions(res.data?.draft_carts || []);
    } catch (e) {
      console.warn('Fetch venue draft carts failed:', e);
      setTransactions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [venueID, token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchTransactions();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchTransactions]);

  // ── Edición de líneas del carrito persistido (misma UX que el carrito del Dispensario/Barra) ──
  // +/- en productos por unidad, borrar línea (gramos o unidades), vaciar el carrito.
  // Cada acción re-postea el carrito completo a /v1/draft-cart (que libera y vuelve a
  // reservar stock); vaciar = DELETE. Optimista en local + refetch silencioso para reconciliar.
  const [savingCartId, setSavingCartId] = useState(null);
  const savingCartIdRef = useRef(null);   // #487: candado síncrono contra solapes

  const sameLine = (a, b) =>
    String(a.product_id) === String(b.product_id) &&
    String(a.location_id ?? '') === String(b.location_id ?? '');

  // Recalcula subtotal/descuento/total de un carrito tras editar sus líneas (preview local).
  // El descuento por línea = manual de la línea y, sobre lo que queda, el del socio.
  const recomputeCart = (tx, newItems) => {
    // La etiqueta se lee con el parser COMPARTIDO: el privado que había aquí
    // leía «20% + 10%» como 20% y el cobro aplica 30% (suma con tope).
    let memberRate = parseMemberDiscount(tx.discount_label);
    // Fallback para carritos sin etiqueta: ratio original (válido si aún no hay manual).
    if (!memberRate && Number(tx.subtotal) > 0 && !newItems.some((i) => i.manual_discount)) {
      memberRate = Number(tx.discount_amount || 0) / Number(tx.subtotal);
    }
    // La MISMA fórmula que el POS y la Sala (lib/cartTotals): era la cuarta
    // copia a mano de esta cuenta, y las copias son como se cuelan los descuadres.
    // Traducción en la frontera (patrón del POS al cargar carritos): la fórmula
    // compartida habla UNA sola grafía {price, quantity, manual_discount}.
    const t = calcularTotalesCarrito(
      newItems.map((i) => ({ price: i.unit_price, quantity: i.quantity, manual_discount: i.manual_discount })),
      memberRate,
    );
    return { ...tx, items: newItems, subtotal: t.subtotal, discount_amount: t.descuento, amount: t.total, total: t.total, items_count: newItems.length };
  };

  const persistCart = useCallback(async (tx, newItems) => {
    // #487 (punto 2): esta pantalla guardaba SIN compare-and-swap — el camino más
    // expuesto a resucitar un carrito ya cobrado. Ahora: (1) un solo guardado en
    // vuelo por carrito, (2) se lee la versión REAL justo antes y viaja como
    // base_version, (3) un 409 no pisa nada — recarga y avisa.
    if (savingCartIdRef.current === tx.id) return;   // guardado en vuelo: no solapar
    const items = newItems
      .filter((i) => (Number(i.quantity) || 0) > 0)
      .map((i) => ({
        product_id: i.product_id,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price) || 0,
        real_weight: i.real_weight != null ? Number(i.real_weight) : null,
        location_id: i.location_id ?? null,
      }));
    savingCartIdRef.current = tx.id;
    setSavingCartId(tx.id);
    try {
      if (items.length === 0) {
        await fetch(
          `/v1/draft-cart?user_id=${tx.user_id}&venue_id=${venueID}&cart_type=${tx.cart_type}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // La versión vigente, del servidor — no de la lista (que puede ser vieja).
        let baseVersion = null;
        try {
          const vres = await fetch(
            `/v1/draft-cart?user_id=${tx.user_id}&venue_id=${venueID}&cart_type=${tx.cart_type}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (vres.ok) baseVersion = (await vres.json())?.version ?? 0;
        } catch { /* sin versión → guardado legacy sin CAS, como antes */ }

        const res = await fetch('/v1/draft-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            user_id: tx.user_id, venue_id: venueID, cart_type: tx.cart_type, items,
            ...(baseVersion != null ? { base_version: baseVersion } : {}),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = err?.detail;
          if (res.status === 409 && detail?.code === 'cart_version_conflict') {
            // Otra pantalla (el POS, otra tablet) cambió el carrito: NO pisamos.
            alert('El carrito cambió en otra pantalla. Se recarga con el estado real.');
            return;
          }
          throw new Error(typeof detail === 'string' ? detail : (detail?.msg || 'No se pudo actualizar el carrito'));
        }
      }
    } catch (e) {
      alert(e.message || 'No se pudo actualizar el carrito');
    } finally {
      savingCartIdRef.current = null;
      setSavingCartId(null);
      await fetchTransactions(true);
      avisarCarritosCambiados();
    }
  }, [venueID, token, fetchTransactions]);

  const editCartLines = (tx, newItems) => {
    setTransactions((prev) => prev.map((t) => (t.id === tx.id ? recomputeCart(t, newItems) : t)));
    persistCart(tx, newItems);
  };

  const handleItemQty = (tx, item, delta) => {
    const newItems = (tx.items || [])
      .map((i) => (sameLine(i, item) ? { ...i, quantity: +(Number(i.quantity) + delta).toFixed(3) } : i))
      .filter((i) => (Number(i.quantity) || 0) > 0);
    editCartLines(tx, newItems);
  };

  const handleItemRemove = (tx, item) => {
    const newItems = (tx.items || []).filter((i) => !sameLine(i, item));
    editCartLines(tx, newItems);
  };

  // Descuento manual de la línea (%). Solo estado local (el draft del servidor no lo
  // guarda); se recalcula el total y viaja al cobro (PaymentModal → make-purchase).
  // No re-postea el carrito para no perderlo en el refetch.
  const handleItemDiscount = (tx, item, value) => {
    const raw = value === '' || value == null ? undefined : Math.min(100, Math.max(0, Number(value)));
    const newItems = (tx.items || []).map((i) => (sameLine(i, item) ? { ...i, manual_discount: raw } : i));
    setTransactions((prev) => prev.map((t) => (t.id === tx.id ? recomputeCart(t, newItems) : t)));
  };

  // Lista de líneas editable (reutilizada en tarjeta móvil y tabla desktop)
  const renderItemsEditor = (tx) => {
    const saving = savingCartId === tx.id;
    if (!tx.items || tx.items.length === 0) {
      return <div className="mt-2 text-gray-400 text-xs">Sin items</div>;
    }
    return (
      <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-2">
        {tx.items.map((item, index) => {
          const isWeight = item.unit_type === 'weight';
          const lineTotal = (Number(item.quantity) || 0) * parseFloat(item.unit_price || 0);
          return (
            <div key={index} className="border-b border-gray-200 pb-2 last:border-b-0 last:pb-0">
              {/* Nombre + borrar */}
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium truncate">{item.product_name || `Producto #${item.product_id}`}</span>
                <button type="button" disabled={saving} onClick={() => handleItemRemove(tx, item)} title="Eliminar línea" aria-label="Eliminar línea" className="flex-shrink-0 text-red-600 hover:text-red-700 p-0.5 disabled:opacity-50">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                </button>
              </div>
              {/* Lista: una fila por dato (etiqueta · valor) */}
              <div className="mt-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Precio</span>
                  <span className="text-gray-700 whitespace-nowrap">€{parseFloat(item.unit_price || 0).toFixed(2)}/{isWeight ? 'g' : 'u'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Cantidad</span>
                  {isWeight ? (
                    // Por gramo: el peso se fija con la báscula al añadir; aquí no se ajusta.
                    <span className="inline-flex flex-col items-end rounded-md border border-gray-300 px-2 py-1 tabular-nums">
                      <span>{cantidadDespachada(item)}<small className="ml-0.5 text-gray-500">g</small></span>
                      {notaPedidoMerma(item) && <small className="text-[10px] text-gray-500">{notaPedidoMerma(item)}</small>}
                    </span>
                  ) : (
                    <div className="inline-flex items-center rounded-md border border-gray-300">
                      <button type="button" disabled={saving} onClick={() => handleItemQty(tx, item, -1)} className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50" aria-label="Quitar uno">–</button>
                      <span className="px-1 tabular-nums min-w-[40px] text-center">{item.quantity}<small className="ml-0.5 text-gray-500">u</small></span>
                      <button type="button" disabled={saving} onClick={() => handleItemQty(tx, item, 1)} className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50" aria-label="Añadir uno">+</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Dto.</span>
                  {/* Descuento manual de la línea (%): se encadena con el del socio. 100% = regalar. */}
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={item.manual_discount ?? ''}
                      onChange={(e) => handleItemDiscount(tx, item, e.target.value)}
                      placeholder="0"
                      className="w-12 rounded border border-gray-300 px-1 py-0.5 text-right tabular-nums focus:border-lime-400 focus:outline-none"
                    />
                    <span>%</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Total</span>
                  <span className="font-semibold text-gray-900">€{lineTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handlePagar = (tx, socio) => {
    setPaymentTx({ ...tx, _socio: socio, _carritos: [tx] });
  };

  // "Pagar Todo" — combine all carts for a given user into one payment
  const handlePagarTodo = (userId, socio) => {
    const userTxs = transactions.filter((t) => t.user_id === userId);
    if (userTxs.length === 0) return;

    const combinedItems = userTxs.flatMap((t) => (t.items || []).map((i) => ({ ...i, _cartType: t.cart_type })));
    const combinedTotal = userTxs.reduce((s, t) => s + parseFloat(t.amount ?? t.total ?? 0), 0);
    const combinedSubtotal = userTxs.reduce((s, t) => s + parseFloat(t.subtotal ?? t.amount ?? t.total ?? 0), 0);
    const combinedDiscountAmount = userTxs.reduce((s, t) => s + parseFloat(t.discount_amount ?? 0), 0);
    const cartTypes = [...new Set(userTxs.map((t) => t.cart_type))];
    const discountLabels = [...new Set(userTxs.map((t) => t.discount_label).filter(Boolean))];

    setPaymentTx({
      user_id: userId,
      user_name: userTxs[0].user_name,
      user_email: userTxs[0].user_email,
      items: combinedItems,
      amount: combinedTotal,
      subtotal: combinedSubtotal,
      discount_amount: combinedDiscountAmount,
      discount_label: discountLabels.length > 0 ? discountLabels[0] : null,
      // 'combined' cuando hay 2+ tipos → el backend consume AMBOS drafts (dispensario
      // + productos) y no re-descuenta. Un solo tipo = ese cart_type.
      cart_type: cartTypes.length === 1 ? cartTypes[0] : 'combined',
      _allCartTypes: cartTypes,
      _isCombined: true,
      _socio: socio,
      _carritos: userTxs,
    });
  };

  const handlePaymentComplete = async (result) => {
    const tx = paymentTx;
    if (
      result?.method === 'credit' &&
      tx?.user_id != null &&
      result.newBalance != null &&
      Number.isFinite(Number(result.newBalance))
    ) {
      updateUserCredit(tx.user_id, result.newBalance);
      await refreshUsersInClub({
        creditPatch: { userId: tx.user_id, balance: result.newBalance },
      });
    }
    if (paymentTx?._isCombined && paymentTx?._allCartTypes) {
      // If combined payment, delete all draft carts for the user
      for (const ct of paymentTx._allCartTypes) {
        try {
          await fetch(
            `/v1/draft-cart?user_id=${paymentTx.user_id}&venue_id=${venueID}&cart_type=${ct}`,
            { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) {
          console.warn('Delete draft cart failed:', e);
        }
      }
    } else if (paymentTx?.cart_type) {
      // If single payment, delete the specific draft cart
      try {
        await fetch(
          `/v1/draft-cart?user_id=${paymentTx.user_id}&venue_id=${venueID}&cart_type=${paymentTx.cart_type}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.warn('Delete draft cart failed:', e);
      }
    }
    setPaymentTx(null);
    await fetchTransactions();
    avisarCarritosCambiados();
    // Sin navigate: te quedas en Carritos con la lista ya refrescada — el
    // carrito pagado desaparece solo (petición Kong Club, 14-ago).
  };

  // Group transactions by user_id to show "Pagar Todo" when a user has multiple carts
  const userCartCounts = {};
  for (const tx of transactions) {
    userCartCounts[tx.user_id] = (userCartCounts[tx.user_id] || 0) + 1;
  }

  // Filter by user when coming from Entradas (e.g. /carritos?user_id=xxx)
  const displayedTransactions = filterUserId
    ? transactions.filter((t) => t.user_id === filterUserId)
    : transactions;
  const filterUserName = filterUserId && displayedTransactions.length
    ? displayedTransactions[0].user_name || displayedTransactions[0].user_email
    : null;

  // OJO con `flex-1` aquí: estos botones viven en DOS contenedores distintos —los
  // dos de pagar por área van en una fila, y «Pagar todo» y «Vaciar» cuelgan
  // directamente de la columna—. En una columna el eje principal es el vertical,
  // así que `flex-1` (que trae `flex-basis: 0`) le gana al `h-9` y los aplasta:
  // salían visiblemente más bajos que los de arriba (reporte del usuario, 18-ago).
  // El reparto horizontal se pide donde de verdad hace falta: en la fila.
  const base =
    'inline-flex h-9 items-center justify-center rounded-sm text-sm font-medium ' +
    'transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const btnOutline = `${base} border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] hover:border-[var(--border-hover)]`;
  const btnPrimary = `${base} bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-strong)]`;
  const btnDestructive = `${base} bg-[var(--destructive-bg)] text-[var(--destructive)] hover:bg-[var(--destructive-border)]`;

  const getTypeLabel = (cartType) =>
    cartType === 'productos' ? 'Productos' : 'Dispensario';

  // ── Agrupado por SOCIO (Figma 3273:26663) ──────────────────────────────────
  // La rejilla muestra una tarjeta por socio, no por carrito: un mismo socio
  // puede deber uno del dispensario y otro de la barra, y quien cobra piensa en
  // «lo que debe Pablo», no en dos apuntes sueltos. De ahí el total sumado y el
  // «Pagar todo», que ya existía en el código pero estaba enterrado en la tabla.
  const socios = (() => {
    const porSocio = new Map();
    for (const tx of displayedTransactions) {
      const clave = String(tx.user_id);
      if (!porSocio.has(clave)) {
        // `venue-draft-carts` no manda ni foto ni estado del socio: solo
        // nombre, email y membership_id. La foto se saca de los socios en
        // sala, que ya viven en esta pantalla — si el socio se fue, la
        // tarjeta se queda sin foto en vez de inventarse una.
        const enSala = (usersInVenue || []).find((u) => String(u.id) === clave);
        porSocio.set(clave, {
          id: tx.user_id,
          name: tx.user_name || tx.user_email || 'Socio',
          number: socioNum(tx) ?? null,
          status: enSala ? 'En sala' : null,
          photo: enSala?.profile?.image_path || null,
          carritos: [],
          total: 0,
        });
      }
      const s = porSocio.get(clave);
      s.carritos.push(tx);
      s.total += parseFloat(tx.amount ?? tx.total ?? 0);
    }
    return [...porSocio.values()];
  })();

  const termino = busqueda.trim();
  const coincide = filtroBusqueda(termino);
  const sociosVisibles = termino ? socios.filter((s) => coincide([s.name, s.number])) : socios;

  // Los carritos que hay DETRÁS de las tarjetas visibles: un socio puede tener dos.
  const carritosVisibles = sociosVisibles.reduce((n, s) => n + (s.carritos?.length || 0), 0);

  const carritoDe = (socio, tipo) => socio.carritos.find((t) => t.cart_type === tipo);

  /** Vaciar EXACTAMENTE los carritos que se le pasan, y decir cuáles son.
   *
   *  Antes solo existía la versión "todos los del socio", y el panel de cobro la
   *  llamaba también cuando se había abierto para cobrar UNA sola área: vaciar
   *  desde ahí se llevaba por delante el carrito de la otra, que no estaba ni a la
   *  vista. El stock vuelve, así que no se pierde género — pero el pedido hay que
   *  rehacerlo, y el aviso decía "los carritos" en plural sin que el operario
   *  tuviera motivo para esperarlo. */
  const vaciarCarritos = async (carritos, nombre) => {
    const lista = (carritos || []).filter(Boolean);
    if (lista.length === 0) return;
    const que = lista.length > 1
      ? 'los carritos'
      : `el carrito de ${getTypeLabel(lista[0].cart_type)}`;
    // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
    // club y «Vaciar» moría sin decir nada (auditoría de UI, UI-003).
    const vaciar = await confirmar({
      title: `Vaciar ${que}`,
      description: `Se vaciará ${que} de ${nombre} y volverá al stock todo lo reservado.`,
      confirmLabel: 'Vaciar',
      tone: 'danger',
    });
    if (!vaciar) return false;
    lista.forEach((tx) => editCartLines(tx, []));
    return true;
  };

  const vaciarTodosLosDe = (socio) => vaciarCarritos(socio.carritos, socio.name);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center p-8">
          <p className="text-sm text-[var(--muted-foreground)]">Cargando…</p>
        </div>
      );
    }

    if (sociosVisibles.length === 0) {
      // Tres vacíos distintos, porque la salida de cada uno es distinta: no hay
      // nada que cobrar, este socio no debe nada, o la búsqueda no encuentra.
      if (termino) {
        return (
          <EmptyState
            variant="noResults"
            title="Ningún socio coincide con la búsqueda"
            actions={[{ label: 'Limpiar búsqueda', onClick: () => setBusqueda('') }]}
          />
        );
      }
      if (filterUserId) {
        return (
          <EmptyState
            title="Este socio no tiene carritos pendientes"
            actions={[{ label: 'Ver todos', onClick: () => setSearchParams({}) }]}
          />
        );
      }
      return (
        <EmptyState
          icon={<ShoppingCart size={48} strokeWidth={1} aria-hidden />}
          title="No hay carritos pendientes de pago"
        />
      );
    }

    return (
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 2xl:grid-cols-3">
        {sociosVisibles.map((socio) => {
          const dispensario = carritoDe(socio, 'dispensario');
          const barra = carritoDe(socio, 'productos');
          const guardando = socio.carritos.some((t) => savingCartId === t.id);
          const abierta = String(abierto) === String(socio.id);

          return (
            <CartCard
              key={socio.id}
              member={socio}
              total={socio.total}
              count={socio.carritos.length}
              expanded={abierta}
              onToggle={() => setAbierto(abierta ? null : socio.id)}
            >
              <div className="flex flex-col gap-2">
                {/* Solo se ofrece pagar el área en la que el socio DEBE algo. El
                    frame dibuja los dos botones siempre, pero un «Pagar Barra»
                    que no cobra nada es un botón que miente. */}
                {(dispensario || barra) && (
                  <div className="flex gap-2">
                    {dispensario && (
                      <button
                        type="button"
                        disabled={guardando}
                        onClick={() => handlePagar(dispensario, socio)}
                        className={`${btnOutline} flex-1`}
                      >
                        Pagar Dispensario
                      </button>
                    )}
                    {barra && (
                      <button
                        type="button"
                        disabled={guardando}
                        onClick={() => handlePagar(barra, socio)}
                        className={`${btnOutline} flex-1`}
                      >
                        Pagar Barra
                      </button>
                    )}
                  </div>
                )}

                {/* «Pagar todo» solo cuando hay más de un carrito: con uno solo
                    sería el mismo cobro con dos nombres. */}
                {socio.carritos.length > 1 && (
                  <button
                    type="button"
                    disabled={guardando}
                    onClick={() => handlePagarTodo(socio.id, socio)}
                    className={btnPrimary}
                  >
                    Pagar todo
                  </button>
                )}

                <button
                  type="button"
                  disabled={guardando}
                  onClick={() => vaciarTodosLosDe(socio)}
                  className={btnDestructive}
                >
                  {socio.carritos.length > 1 ? 'Vaciar carritos' : 'Vaciar carrito'}
                </button>

                {/* El detalle de líneas no está en el frame de la tarjeta —vive en
                    el panel lateral, que aún no existe en código— pero editar
                    cantidades y descuentos desde aquí es función real que se usa,
                    así que se conserva plegada para no romper la tarjeta. */}
                {socio.carritos.map((tx) => (
                  <details key={tx.id} className="mt-1">
                    <summary className="cursor-pointer text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]">
                      {socio.carritos.length > 1 ? `${getTypeLabel(tx.cart_type)} · ` : ''}
                      Ver productos ({(tx.items || []).length})
                    </summary>
                    {renderItemsEditor(tx)}
                  </details>
                ))}
              </div>
            </CartCard>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3 p-2 sm:p-4">
      {/* Sale context bar (Figma 3273:27101): título, cuántos y buscador. El
          título dice de quién son los carritos cuando se llega filtrado desde
          Entradas, y ahí aparece «Ver todos» para quitar el filtro. */}
      {/* Cuenta CARRITOS, no tarjetas. Contaba socios y lo llamaba "carritos": con
          un socio que debía dos cosas ponía "1 carrito" mientras su propia tarjeta
          decía "2 carritos" y el menú marcaba 2 (reporte del usuario, 19-ago).
          Tres números distintos para lo mismo, y ninguno mentía del todo. */}
      <SectionHeader
        title={filterUserId ? `Carritos de ${filterUserName || 'este socio'}` : 'Carritos pendientes'}
        count={`${carritosVisibles} ${carritosVisibles === 1 ? 'carrito' : 'carritos'}`}
        sticky={false}
        actionsClassName="min-w-0 items-center lg:flex-1 lg:justify-end"
      >
        <div className="flex w-full min-w-0 items-center gap-3">
          {/* Jerarquía de encogido (mismo bug que Control a 1024): el ancho tope
              vive en el WRAPPER (encogible) y el input siempre w-full. Un ancho
              fijo aquí pisaba el título en iPad. */}
          <div className="relative min-w-0 flex-1 sm:max-w-[344px]">
            <Search
              size={16}
              strokeWidth={1.5}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
            />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar socio"
              aria-label="Buscar socio"
              className="h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--background)] pl-9 pr-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:outline-none"
            />
          </div>
          {filterUserId && (
            <Button variant="outline" className="shrink-0 px-3" onClick={() => setSearchParams({})}>
              Ver todos
            </Button>
          )}
        </div>
      </SectionHeader>

      {renderContent()}

      {paymentTx && (
        <CarritoPanel
          guardando={savingCartId != null}
          socio={paymentTx._socio}
          carritos={paymentTx._carritos || []}
          token={token}
          venueId={venueID}
          onClose={() => setPaymentTx(null)}
          onPaid={handlePaymentComplete}
          onItemQty={handleItemQty}
          onItemRemove={handleItemRemove}
          onClear={async () => {
            // Solo lo que este panel está cobrando: si se abrió para el Dispensario,
            // el carrito de Barra no se toca. El panel se cierra solo si se vació:
            // cancelar la pregunta deja al operario donde estaba.
            const vaciado = await vaciarCarritos(paymentTx._carritos || [], paymentTx._socio?.name);
            if (vaciado) setPaymentTx(null);
          }}
          onContinue={() => {
            // «Seguir añadiendo» lleva al área de la que viene el carrito, con el
            // socio ya puesto: es el mismo sitio donde se armó.
            const tipo = (paymentTx._carritos || [])[0]?.cart_type;
            const modo = tipo === 'productos' ? '&mode=productos' : '';
            navigate(`/dispenser?userId=${paymentTx._socio?.id}${modo}`);
          }}
        />
      )}
      {dialogoConfirmar}
    </div>
  );
}
